import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Dot } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2, AlertTriangle, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Main Website", "Landing Page", "Institute Site", "CRM", "Booking System", "Social Page", "Other"];
const CHECKS = [["offers_updated", "Offers"], ["doctor_profiles", "Doctor profiles"], ["before_after", "Before/After"], ["forms_working", "Forms"], ["tracking_pixels", "Pixels"], ["whatsapp_button", "WhatsApp"]];

export default function DigitalAssets() {
  const { centers } = useApp();
  const { effectiveRole } = useAuth();
  const canWrite = effectiveRole !== "viewer";
  const [assets, setAssets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, al] = await Promise.all([api.get("/assets"), api.get("/assets-alerts")]);
    setAssets(a.data); setAlerts(al.data);
  };
  useEffect(() => { load(); }, []);

  const start = (a) => {
    setForm(a || { name: "", type: "Landing Page", url: "", owner: "", center_id: centers[0]?.id, platform: "", ssl_expiry: "", domain_expiry: "", freshness_days: 30, last_content_update: new Date().toISOString().slice(0, 10), checklist: {}, status: "up" });
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const c = centers.find((x) => x.id === form.center_id);
      const payload = { ...form, center_code: c?.code, freshness_days: Number(form.freshness_days) };
      if (form.id) await api.put(`/assets/${form.id}`, payload);
      else await api.post("/assets", payload);
      toast.success("Asset saved"); setOpen(false); setTimeout(load, 250);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (a) => {
    try { await api.delete(`/assets/${a.id}`); toast.success("Asset deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="digital-assets-page">
      <PageHeader eyebrow="Infrastructure" title="Digital Assets Monitor"
        subtitle="Uptime checks every 15 min, SSL/domain expiry alerts, content freshness and per-asset checklists."
        action={<Button onClick={() => start(null)} data-testid="add-asset-button" style={{ display: canWrite ? undefined : "none" }}><Plus className="h-4 w-4 mr-1" /> Add Asset</Button>} />

      {alerts.length > 0 && (
        <Card className="p-4 mb-4 border-amber-300 bg-amber-50 dark:bg-amber-950/30">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-amber-600" /><p className="text-sm font-semibold text-amber-800 dark:text-amber-300">{alerts.length} active alerts</p></div>
          <div className="flex flex-wrap gap-2">
            {alerts.slice(0, 8).map((al, i) => (
              <Badge key={i} variant="outline" className={al.severity === "red" ? "border-rose-300 text-rose-700" : "border-amber-300 text-amber-700"} data-testid={`asset-alert-${i}`}>
                {al.asset}: {al.detail}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Asset</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead>
            <TableHead>Resp.</TableHead><TableHead>30d Uptime</TableHead><TableHead>SSL</TableHead>
            <TableHead>Freshness</TableHead><TableHead>Checklist</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {assets.map((a) => {
              const okChecks = Object.values(a.checklist || {}).filter(Boolean).length;
              return (
                <TableRow key={a.id} data-testid={`asset-row-${a.id}`}>
                  <TableCell>
                    <div className="font-medium flex items-center gap-1.5">{a.name}
                      <a href={a.url} target="_blank" rel="noreferrer" className="text-muted-foreground"><ExternalLink className="h-3 w-3" /></a></div>
                    <div className="text-[11px] text-muted-foreground">{a.owner} · {a.center_code}</div>
                  </TableCell>
                  <TableCell className="text-xs">{a.type}</TableCell>
                  <TableCell><div className="flex items-center gap-1.5"><Dot status={a.status === "up" ? "green" : "red"} /><span className="text-xs capitalize">{a.status || "—"}</span></div></TableCell>
                  <TableCell className="text-xs tabular-nums">{a.response_time ? `${a.response_time}ms` : "—"}</TableCell>
                  <TableCell className="text-xs tabular-nums">{a.uptime_30d ?? "—"}%</TableCell>
                  <TableCell className="text-xs">{a.ssl_expiry || "—"}</TableCell>
                  <TableCell className="text-xs">{a.last_content_update}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-[10px]">{okChecks}/6</Badge></TableCell>
                  <TableCell>
                    <div className="flex">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => start(a)} data-testid={`edit-asset-${a.id}`}><ExternalLink className="h-0 w-0 hidden" />✎</Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => remove(a)} data-testid={`delete-asset-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="asset-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>{form.id ? "Edit Asset" : "Add Asset"}</DialogTitle>
            <DialogDescription>Register a website, landing page, CRM or booking system to monitor.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="asset-name-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">URL</Label><Input value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="asset-url-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Owner</Label><Input value={form.owner || ""} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Center</Label>
                <Select value={form.center_id} onValueChange={(v) => setForm({ ...form, center_id: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{centers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs">SSL expiry</Label><Input type="date" value={form.ssl_expiry || ""} onChange={(e) => setForm({ ...form, ssl_expiry: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Domain expiry</Label><Input type="date" value={form.domain_expiry || ""} onChange={(e) => setForm({ ...form, domain_expiry: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Last content update</Label><Input type="date" value={form.last_content_update || ""} onChange={(e) => setForm({ ...form, last_content_update: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Freshness threshold (days)</Label><Input type="number" value={form.freshness_days ?? 30} onChange={(e) => setForm({ ...form, freshness_days: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Update checklist</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {CHECKS.map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={!!(form.checklist || {})[key]} onCheckedChange={(v) => setForm({ ...form, checklist: { ...(form.checklist || {}), [key]: !!v } })} data-testid={`check-${key}`} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="save-asset-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
